import { ServerConfigContext } from '@src/contexts/server-config-context';
import { useCallback, useContext } from 'react';

export const useRouteUtils = () => {
  const { proxyBasePath } = useContext(ServerConfigContext);

  /**
   * Given an absolute URL path to an internal resource (one served by this application), returns a version with the
   * proxy base path (any base path under which the application is being served under) prepended. It's important to use
   * this whenever an absolute path is referencing an internal resource or endpoint, so that when the application is
   * being hosted under a reverse proxy's base path (e.g. a feature branch deployment in the APT Shared Cluster), this
   * utility will keep those calls pointing at this deployment.
   *
   * In our insurance repo's setup, this method should be called whenever an internal link is being generated for href
   * or link elements. It's the responsibility of each component to use `getInternalPath()` for internal resources
   * and not use it for external ones. If `getInternalPath()` is mistakenly not used for an internal resource,
   * then feature branch deployments will be sending traffic to that resource on a different deployment (the mainline
   * deployment in a given Chewy environment), which can invalidate feature branch testing. If `getInternalPath()` is
   * mistakenly used for a resource that isn't served by this application, it will likely just 404.
   *
   * This method SHOULD NOT be called in conjunction with using WindowService.redirectTo, WindowService.replace, or useFetch()
   * since the logic to prepend the proxy base path is already handled in those methods. Calling this method in conjunction
   * with any of the aforementioned will result in the proxy base path being prepended twice which will break routing.
   */
  const getInternalPath = useCallback(
    (absoluteUrlPath: string) => {
      if (!absoluteUrlPath?.startsWith('/')) {
        throw new Error(
          'getInternalPath should only be used with absolute URL paths which begin with a slash.',
        );
      }
      return `${proxyBasePath || ''}${absoluteUrlPath}`;
    },
    [proxyBasePath],
  );

  return {
    getInternalPath,
  };
};
